//Data record types
export interface InsuranceCasesOverview {
  segment: string
  count: number
}
export interface ContractOverview {
  segment: string
  count: number
}
export enum MessageType {
  sent = 'sent',
  received = 'received',
}

export interface MessagesOverview {
  type: MessageType
  count: number
}

export interface AccountManager {
  name: string
  email: string
  phone: string
  initials: string
  classification: string
  classificationType?: ClassificationType
}

export interface Notice {
  id: number
  severity: number
  description: string
}

export enum MessageStatus {
  created = 10,
  read = 20,
  archived = 30,
}

export enum MessageBindingType {
  contract = 'S',
  subcontract = 'A',
  insuranceCase = 'P',
}

export interface Message {
  id: number
  description: string
  status: MessageStatus
  startDate: Date
  fullDescription?: string
  category?: string
  completionDate?: Date
  bindingType?: MessageBindingType
  bindingId?: number
  bindingText?: string
  contractType?: ContractType
  type: MessageType
}

export interface Client {
  id: number
  name: string
}

export interface Address {
  type: string
  street: string
  city: string
  zipCode: string
}

export interface ClientInformation {
  name: string
  type: string
  ic: string
  birthDate: Date | null
  phones: string[]
  emails: string[]
  addresses: Address[]
}

export interface Contract {
  id: number
  /**
   * Nadřazená smlouva (pro podsmlouvy)
   */
  parentContractId?: number
  /**
   * Typ nadřazené smlouvy (R/S/K) převedený na ContractType
   */
  parentContractType?: ContractType
  /**
   * Číslo smlouvy
   */
  contractNumber: string
  /**
   * Typ smlouvy R-Ramcova, S - Samostatná
   */
  type: ContractType
  /**
   * Pojišťovna
   */
  partner: string
  status: ContractStatus
  /**
   * Počátek pojišťení
   */
  startDate: Date | null
  /**
   * Konec pojišťení
   */
  endDate: Date | null
  segment: string
  subsegment: string
  product: string
  attributes: ContractAttribute[]
  validity: ContractValidity
  /**
   * Důvod stornování smlouvy - zobrazovat pouze pokud je smlouva stornovaná (Pokud není prázdný)
   */
  cancellationReason: string | null
}
export interface ContractDetail extends Contract {
  // Additional detailed properties for contract detail view
  /**
   * Pojistník
   */
  policyholder: string
  /**
   * Datum obnovy smlouvy
   */
  renewalDate: Date | null
  /**
   * Roční pojistné
   */
  annualPremium: number
  /**
   * Lhůtní pojistné (rozpočítané roční pojistné dle splatnosti)
   */
  premium: number
  /**
   * Splatnost smlouvy
   */
  paymentFrequency: string
  accountManager: AccountManager[]
  risks: Risk[]
}
export interface ContractAttribute {
  name: string
  value: string
  toolTip?: string
}

export enum ContractValidity {
  term = 'DU', // Dobou urcenou
  indefinite = 'DN', // Dobou neurcenou
  revolving = 'AP', // Automatické prodloužení smlouvy
}

export enum ContractType {
  multi = 'R', // Ramcova
  single = 'S', // Samostatna
  kasko = 'K', // Kasko
  subcontract = 'P', // Podsmlouva
}

export interface Risk {
  /**
   * Popis rizika
   */
  description: string
  /**
   * Limit rizika
   */
  limit: string
  /**
   * Částka rizika
   */
  amount: number
  /**
   * Podíl rizika
   */
  share: string
  /**
   * Rozsah rizika
   */
  scope: string
}

export interface Document {
  id: number
  /**
   * Identifikátor UID
   */
  uid: string
  /**
   * Název souboru
   */
  name: string
  /**
   * Přípona souboru
   */
  extension: string
  /**
   * Velikost souboru
   */
  size: number
  /**
   * Poznámka
   */
  note: string
  /**
   * Typ přílohy
   */
  type?: string | null
  /**
   * Identifikátor podkladu (PrilohaPu only)
   */
  podkladID?: number
  /**
   * Vyžadováno - příznak zda byla příloha vyžadována (PrilohaPu only)
   */
  required?: boolean
  /**
   * Doloženo - příznak zda byla příloha doložena (PrilohaPu only)
   */
  submitted?: boolean
}

export enum ContractStatus {
  active,
  inactive,
  cancelled,
}

//Response types
export interface Response {
  success: boolean
  message?: string
}
export interface GetDashboardInfoResponse extends Response {
  dashboard?: {
    accountManager: AccountManager[]
    contractsOverview: ContractOverview[]
    insurancesOverview: InsuranceCasesOverview[]
    messagesOverview: MessagesOverview[]
    noticeBoard: Notice[]
  }
}
export interface GetClientsForCustomerResponse extends Response {
  clients?: Client[]
}
export interface GetClientInformationResponse extends Response {
  clientInformation?: ClientInformation
}
export interface GetContractsResponse extends Response {
  contracts?: Contract[]
}

export interface GetInsuranceCasesResponse extends Response {
  insuranceCases?: InsuranceCase[]
}

export interface GetMessagesResponse extends Response {
  messages?: Message[]
}

export interface GetInsuranceCaseDetailResponse extends Response {
  insuranceCase?: InsuranceCaseDetail
}

export interface GetSubcontractsResponse extends Response {
  subcontracts?: Contract[]
}
export interface GetDocumentsResponse extends Response {
  documents?: Document[]
}

export interface InsuranceCase {
  /**
   * Typ smlouvy
   */
  contractType: ContractType
  /**
   * Identifikátor události
   */
  id: number
  /**
   * Číslo pojistné události
   */
  caseNumber: string
  /**
   * Stav pojistné události 30 - Otevřená, 40 - Uzavřená, 50 - Odložená
   */
  status: InsuranceCaseStatus
  /**
   * Číslo hlášenky
   */
  reportNumber: string
  /**
   * Datum vzniku pojistné události
   */
  date: Date | null
  /**
   * Datum nahlášení
   */
  reportDate: Date | null
  /**
   * Datum ukončení pojistné události
   */
  endDate: Date | null
  /**
   * Identifikator - vazba na smlouvu
   */
  contractId: number | null
  /**
   * Identifikátor - vazba na atribut
   */
  subcontractId: number | null
  /**
   * Segment pojistné události
   */
  segment?: string | null
  /**
   * Pojišťovna
   */
  partner?: string | null
  /**
   * Typ zastoupení - Pojištěný/Poškozený
   */
  representationType?: string | null
  /**
   * Typ skody - Majetek/Odpovědnost/HAV/POV/Škoda na zdraví/Finanční škoda
   */
  damageType?: string | null
  /**
   * RZ vozidla
   */
  RZ?: string | null
  /**
   * VIN vozidla
   */
  Vin?: string | null
  /**
   * Správce pojistné události
   */
  accountManager?: AccountManager[]
  /**
   * Popis místa události
   */
  location?: string | null
  /**
   * Popis příčiny události
   */
  cause?: string | null
  /**
   * Popis události
   */
  description?: string | null
  /**
   * Regres
   */
  regress?: boolean
  /**
   * Identifikátor UID
   */
  uid?: string | null
}

export interface InsuranceCaseDetail extends InsuranceCase {
  /**
   * Seznam účastníků pojistné události
   */
  participants?: Participant[]
  /**
   * Seznam komunikace mezi klientem a crc
   */
  communication?: InsuranceCaseComs[]
  /**
   * Finanční údaje pojistné události
   */
  settlement: Settlement
  /**
   * Seznam poznámek k pojistné události
   */
  notes?: string[]
  /**
   * Seznam plateb
   */
  payments?: Payment[]
}

export interface InsuranceCaseComs {
  id: number
  text: string
  date: Date | null
  note?: string | null
}

export interface Settlement {
  damageEstimate: number | null
  coParticipation: number | null
  totalDamage: number | null
  reserve: number | null
  fulfillmentTotal: number | null
}

export interface Participant {
  id: number
  type?: string | null
  name?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  note: string
}

export enum InsuranceCaseStatus {
  open,
  closed,
  postponed,
}

export enum ClassificationType {
  contractManager,
  insuranceCaseManager,
}

export interface GetContractDetailResponse {
  success: boolean
  contract?: ContractDetail
  message?: string
}

export interface GetPaymentsResponse extends Response {
  payments?: Payment[] | null
}
export interface GetSearchResponse extends Response {
  contracts?: Contract[]
  insuranceCases?: InsuranceCase[]
}
export interface Payment {
  /**
   * Datum úhrady
   */
  paymentDate: Date | null
  /**
   * Období od
   */
  periodFrom: Date | null
  /**
   * Období do
   */
  periodTo: Date | null
  /**
   * Částka úhrady
   */
  amount: number
  /**
   * Poznámka k platbě
   */
  note: string
}
// CustomerDataService interface moved to ./customer-data-service.ts
